import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { uiLogger } from '../utils/logger';

export default function ApiDocsPage(): JSX.Element {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    // Check if API docs are available
    const checkDocs = async () => {
      try {
        const response = await fetch('/docs/api/index.html');
        if (!response.ok) {
          throw new Error('API Documentation not found');
        }
        setIsLoading(false);
      } catch (error) {
        uiLogger.error('Error loading API documentation:', error);
        setDocsError('API Documentation is not available.');
        setIsLoading(false);
      }
    };

    checkDocs();
  }, []);

  const handleIframeLoad = (): void => {
    setIframeLoaded(true);
    uiLogger.info('API documentation iframe loaded successfully');
  };

  const handleIframeError = (): void => {
    uiLogger.error('Error loading API documentation iframe');
    setDocsError('Failed to load API documentation content.');
  };

  const handleBackToDocs = (): void => {
    navigate('/docs');
  };

  const handleBackToHome = (): void => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading API documentation...</p>
        </div>
      </div>
    );
  }

  if (docsError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <main className="container-max section-padding py-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
                </div>
                <div className="ml-4">
                  <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                    API Documentation Not Available
                  </h2>
                  <p className="text-red-700 dark:text-red-300 mb-4">
                    {docsError}
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleBackToDocs}
                      className="btn-primary"
                    >
                      Back to Documentation
                    </button>
                    <button
                      onClick={() => window.location.reload()}
                      className="btn-secondary"
                    >
                      Retry Loading
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Page Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm flex-shrink-0">
        <div className="container-max section-padding">
          <div className="flex items-center justify-between py-3">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              
            </h1>
            <div className="flex gap-4">
              <button
                onClick={handleBackToDocs}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
              >
                Documentation
              </button>
              <button
                onClick={handleBackToHome}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
              >
                Home
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* API Documentation Content - Takes remaining height */}
      <main className="flex-1" style={{ minHeight: 'calc(100vh - 80px)' }}>
        {!iframeLoaded && !docsError && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading API documentation content...</p>
            </div>
          </div>
        )}
        <iframe
          src="/docs/api/index.html"
          className="w-full h-full border-0"
          title="Fabrik 28 API Documentation"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
          loading="eager"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          style={{ 
            minHeight: 'calc(100vh - 80px)',
            backgroundColor: 'white',
            display: iframeLoaded ? 'block' : 'none'
          }}
        />
      </main>
    </div>
  );
}
